import { supabase } from "@/integrations/supabase/client";

export type ResourceRow = {
  id: string;
  slug: string;
  title: string;
  format: string;
  subfolder: string;
  resource_date: string | null;
  themes: string[];
  youtube: string | null;
  featured: boolean;
  duration: string | null;
  reading_time: string | null;
  file_url: string | null;
  cover_accent: string | null;
  cover_image: string | null;
  summary: string | null;
  description: string | null;
  learn: string[];
  published: boolean;
  meta_title: string | null;
  meta_description: string | null;
  created_at: string;
};

export const TEMAS: Record<string, string> = {
  "ia-comex": "IA & Comex",
  duimp: "DUIMP & Compliance",
  "supply-chain": "Supply Chain & Logística",
  cases: "Cases & Empresas",
  mercado: "Mercado & Tendências",
  despachante: "Despachante Aduaneiro",
  produtos: "Produtos Logcomex",
  geopolitica: "Geopolítica",
  financeiro: "Câmbio & Financeiro",
  "comex-tech-forum": "Comex Tech Forum",
};

export const temaLabel = (id: string) => TEMAS[id] ?? id;

export type Formato = {
  id: string;
  label: string;
  plural: string;
  subpasta: string;
  descricao: string;
};

export const FORMATOS: Formato[] = [
  {
    id: "webinar",
    label: "Webinar",
    plural: "Logcomex Talks",
    subpasta: "webinars",
    descricao:
      "Encontros mensais com especialistas do comércio exterior, tecnologia e supply chain.",
  },
  {
    id: "ctf26",
    label: "Comex Tech Forum 2026",
    plural: "Comex Tech Forum 2026",
    subpasta: "comextech2026",
    descricao:
      "Painéis e palestras da edição 2026 do maior evento de tecnologia no comércio exterior do Brasil.",
  },
  {
    id: "ctf25",
    label: "Comex Tech Forum 2025",
    plural: "Comex Tech Forum 2025",
    subpasta: "comextech2025",
    descricao:
      "Entrevistas e painéis do maior evento de tecnologia no comércio exterior do Brasil.",
  },
  {
    id: "use-case",
    label: "Caso de Uso",
    plural: "Casos de Uso",
    subpasta: "use-cases",
    descricao:
      "Demonstrações práticas de como usar a Logcomex.ai na sua operação de comércio exterior.",
  },
  {
    id: "ebook",
    label: "eBook",
    plural: "eBooks",
    subpasta: "ebooks",
    descricao:
      "Estudos e guias práticos para operar comércio exterior com mais previsibilidade e inteligência de mercado.",
  },
  {
    id: "one-page",
    label: "One-Page",
    plural: "One-Pages",
    subpasta: "one-pages",
    descricao:
      "Estudos rápidos com panorama de setores e movimentos do comércio exterior em uma única página.",
  },
  { id: "relatorio", label: "Relatório", plural: "Relatórios", subpasta: "relatorios", descricao: "" },
  { id: "guia", label: "Guia", plural: "Guias", subpasta: "guias", descricao: "" },
];

export const formatoById = (id: string) => FORMATOS.find((f) => f.id === id);
export const formatoBySubpasta = (sub: string) => FORMATOS.find((f) => f.subpasta === sub);
export const subpastaDe = (r: { format: string; subfolder?: string | null }) =>
  r.subfolder || formatoById(r.format)?.subpasta || "recursos";
export const resourceHref = (r: ResourceRow) => `/recursos/${subpastaDe(r)}/${r.slug}`;

export const isReadable = (format: string) =>
  format === "ebook" || format === "one-page" || format === "relatorio" || format === "guia";

export const getYouTubeId = (url?: string | null): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([\w-]{11})/,
    /(?:youtube\.com\/live\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};
export const getYouTubeThumb = (url?: string | null) => {
  const id = getYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};
export const getYouTubeEmbed = (url?: string | null) => {
  const id = getYouTubeId(url);
  if (!id) return null;
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    controls: "1",
    iv_load_policy: "3",
    playsinline: "1",
    color: "white",
  });
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
};

export const formatarData = (iso?: string | null, long = false) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: long ? "long" : "short",
        year: "numeric",
      })
    : "";

export async function listResources(): Promise<ResourceRow[]> {
  const { data, error } = await (supabase as any)
    .from("resources")
    .select("*")
    .eq("published", true)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as ResourceRow[];
}

export async function getResource(
  slug: string,
  opts: { preview?: boolean } = {},
): Promise<ResourceRow | null> {
  let q = (supabase as any).from("resources").select("*").eq("slug", slug);
  if (!opts.preview) q = q.eq("published", true);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return (data ?? null) as ResourceRow | null;
}
